import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ViewThroughProvider } from "@/lib/viewThroughContext";
import { ViewThroughBanner } from "@/components/portal/ViewThroughBanner";
import { ViewThroughUnsupportedShield } from "@/components/portal/ViewThroughUnsupportedShield";
// EAGER — kept on the main chunk because they are the actual customer-facing
// entry surfaces (`/`, `/v2/c/:campaignId`) and the ambient staff-portal link.
// Everything else moves behind React.lazy so `/v2/c/*` no longer downloads
// the staff-portal + booking-flow + design-system + kiosk modules.
import HomePage from "@/pages/HomePage";
import MarketingV2CustomerRoute from "@/pages/marketing_v2/MarketingV2CustomerRoute";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import PublicLinkRedirect from "@/pages/PublicLinkRedirect";
import StaffPortalCornerLink from "@/components/StaffPortalCornerLink";
import ExitIntentPopup from "@/components/booking/ExitIntentPopup";
import { Suspense, lazy, useState, useEffect } from "react";
import { initAnalytics, trackPageview } from "@/lib/analytics";
import { initPostHogIfEligible } from "@/lib/posthog";

// LAZY — heavy internal surfaces / non-customer routes.
// Owner/staff/portal + kiosk + booking-flow + marketing-v2 workspace all
// only ship to visitors who navigate to those specific paths.
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const StaffPortal = lazy(() => import("@/pages/StaffPortal"));
const BookingFlow = lazy(() => import("@/pages/BookingFlow"));
const ManageBookingPage = lazy(() => import("@/pages/ManageBookingPage"));
const PublicHubPage = lazy(() => import("@/pages/PublicHubPage"));
const SeoServicePage = lazy(() => import("@/pages/SeoServicePage"));
const SeoOfferPage = lazy(() => import("@/pages/SeoOfferPage"));
const CommunicationsPolicy = lazy(() => import("@/pages/CommunicationsPolicy"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const SmsConsent = lazy(() => import("@/pages/SmsConsent"));
const Checkin = lazy(() => import("@/pages/Checkin"));
const MarketingV2Preview = lazy(() => import("@/pages/marketing_v2/MarketingV2Preview"));
const MarketingV2DesignSystem = lazy(() => import("@/pages/marketing_v2/MarketingV2DesignSystem"));
const MarketingV2OwnerAuthoring = lazy(() => import("@/pages/marketing_v2/MarketingV2OwnerAuthoring"));
const MarketingV2LayoutLibrary = lazy(() => import("@/pages/marketing_v2/MarketingV2LayoutLibrary"));
const MarketingV2CrossExperienceLessons = lazy(() => import("@/pages/marketing_v2/MarketingV2CrossExperienceLessons"));
const MarketingV2ReferenceBank = lazy(() => import("@/pages/marketing_v2/MarketingV2ReferenceBank"));
const MarketingV2ServiceAuthorityPreview = lazy(() => import("@/pages/marketing_v2/MarketingV2ServiceAuthorityPreview"));
const MarketingV2DistributionCenter = lazy(() => import("@/pages/marketing_v2/MarketingV2DistributionCenter"));
const MarketingV2CanonicalHubMap = lazy(() => import("@/pages/marketing_v2/MarketingV2CanonicalHubMap"));
const MarketingV2DraftLibrary = lazy(() => import("@/pages/marketing_v2/MarketingV2DraftLibraryPage"));
const MarketingV2CampaignStudio = lazy(() => import("@/pages/marketing_v2/MarketingV2CampaignStudioPage"));
const MarketingV2UnifiedReviews = lazy(() => import("@/pages/marketing_v2/MarketingV2UnifiedReviews"));
const MarketingV2InlineContentEditor = lazy(() => import("@/pages/marketing_v2/MarketingV2InlineContentEditor"));
const BookingWorkspaceNext = lazy(() => import("@/pages/BookingWorkspaceNext"));
const PortfolioWorkspace = lazy(() => import("@/pages/PortfolioWorkspace"));
const LayoutEditorPage = lazy(() => import("@/pages/portal/LayoutEditorPage"));

// Suspense fallback — deliberately minimal so it never adds paint work or
// competes with the LCP element of any real route.
function RouteChunkFallback() {
  return (
    <div
      data-testid="route-chunk-fallback"
      aria-busy="true"
      aria-live="polite"
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#94a3b8",
        fontSize: "14px",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      Loading…
    </div>
  );
}

// Initializes Google + first-party analytics once, then logs a pageview on
// every SPA route change (React Router never reloads, so this is required).
function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    // Route-scoped PostHog init: NEVER loads the SDK on `/v2/c/*` focused
    // customer routes. Preserves full PostHog behavior on every other
    // route. See src/lib/posthog.js for the routing rules.
    initPostHogIfEligible(location.pathname);
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}


function CustomerExitIntent() {
  const location = useLocation();
  const path = location.pathname;
  const [variantId, setVariantId] = useState(null);

  // Poll for variant ID (set asynchronously by HomePage)
  useEffect(() => {
    const check = () => {
      const id = sessionStorage.getItem("lp_variant_id");
      if (id && id !== variantId) setVariantId(id);
    };
    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, [variantId, path]);

  // Only show on customer-facing pages, not on portal/login/register
  const isCustomerPage = path === "/" || path === "/book";
  if (!isCustomerPage) return null;
  // Don't show after booking confirmed
  if (sessionStorage.getItem("booking_confirmed")) return null;
  if (!variantId) return null;
  return <ExitIntentPopup variantId={variantId} />;
}

function App() {
  // ── Custom deals subdomain support ───────────────────────────
  // When the visitor lands on a configured "deals" hostname (e.g.
  // deals.upscaleflowermound.com), render the existing PublicHubPage at "/"
  // pinned to slug="offers" — no redirect, browser URL stays clean, all
  // query params (UTMs, ad_id, cta_id, tq, etc.) are preserved naturally
  // because we never navigate. Other hostnames still see HomePage at "/".
  //
  // Hostnames are sourced from REACT_APP_DEALS_HOSTNAMES (comma-separated)
  // so we can add more without touching code.
  const dealsHostnames = (process.env.REACT_APP_DEALS_HOSTNAMES || "deals.upscaleflowermound.com")
    .split(",")
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  // ── Custom booking subdomain support ─────────────────────────
  // booking.upscaleflowermound.com lands customers directly on the booking
  // flow at "/" — same single-source-of-truth `<BookingFlow />` component
  // used at /book and /portal/receptionist/book. Mirrors the deals pattern.
  const bookingHostnames = (process.env.REACT_APP_BOOKING_HOSTNAMES || "booking.upscaleflowermound.com")
    .split(",")
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  const currentHost = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const isDealsSubdomain = dealsHostnames.includes(currentHost);
  const isBookingSubdomain = bookingHostnames.includes(currentHost);
  const RootElement = isBookingSubdomain
    ? <BookingFlow />
    : isDealsSubdomain
      ? <PublicHubPage forceSlug="offers" />
      : <HomePage />;

  return (
    <div className="min-h-screen bg-background">
      <ViewThroughProvider>
        <BrowserRouter>
          <AnalyticsRouteTracker />
          <ViewThroughBanner />
          <ViewThroughUnsupportedShield />
          <Suspense fallback={<RouteChunkFallback />}>
          <Routes>
          {/* Public Customer Experience */}
          <Route path="/" element={RootElement} />
          <Route path="/book" element={<BookingFlow />} />
          <Route path="/manage-booking" element={<ManageBookingPage />} />
          <Route path="/m/:code" element={<ShortLinkRedirect />} />
          {/* Cascade Pass ⑦ · C4 · Anonymous customer entry for
              campaign-authored tracking links minted as /l/{public_id}.
              See PublicLinkRedirect.jsx header. */}
          <Route path="/l/:public_id" element={<PublicLinkRedirect />} />
          <Route path="/offers" element={<PublicHubPage />} />
          <Route path="/offers/:slug" element={<PublicHubPage />} />

          {/* SEO pages — stable, indexable. Campaign A/B testing stays on
              /book?campaign=<tid> and landing_variants. */}
          <Route path="/services/:slug" element={<SeoServicePage />} />
          <Route path="/offer/:slug" element={<SeoOfferPage />} />

          <Route path="/communications-policy" element={<CommunicationsPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/sms-consent" element={<SmsConsent />} />

          {/* Phase 7 — Self-Check-In Kiosk (customer hospitality surface).
              Standalone shell, no portal chrome. */}
          <Route path="/checkin" element={<Checkin />} />

          {/* Marketing Experience V2 — PUBLIC customer route.
              CRITICAL: session-independent. Renders identically for
              logged-out, Owner, Partner, Standard Receptionist, Limited
              Receptionist. No session-derived rendering. See
              MarketingV2CustomerRoute.jsx header.
              This route is EAGER — kept on the main chunk — so LCP is not
              gated on a chunk fetch. */}
          <Route path="/v2/c/:campaignId" element={<MarketingV2CustomerRoute />} />
          {/* Internal Owner preview surface. Wraps the identical customer
              projection in a preview frame — the frame is the only
              additional element. */}
          <Route path="/portal/marketing-v2/preview/:campaignId" element={<MarketingV2Preview />} />
          <Route path="/portal/marketing-v2/design-system" element={<MarketingV2DesignSystem />} />
          <Route path="/portal/marketing-v2/authoring/:campaignId" element={<MarketingV2OwnerAuthoring />} />
          <Route path="/portal/marketing-v2/layout-library" element={<MarketingV2LayoutLibrary />} />
          <Route path="/portal/marketing-v2/lessons" element={<MarketingV2CrossExperienceLessons />} />
          <Route path="/portal/marketing-v2/reference-bank" element={<MarketingV2ReferenceBank />} />
          <Route path="/portal/marketing-v2/service-authority" element={<MarketingV2ServiceAuthorityPreview />} />
          <Route path="/portal/marketing-v2/distribution" element={<MarketingV2DistributionCenter />} />
          <Route path="/portal/marketing-v2/canonical-hub" element={<MarketingV2CanonicalHubMap />} />
          <Route path="/portal/marketing-v2/studio" element={<MarketingV2DraftLibrary />} />
          <Route path="/portal/marketing-v2/studio/:draftId" element={<MarketingV2CampaignStudio />} />
          <Route path="/portal/marketing-v2/reviews" element={<MarketingV2UnifiedReviews />} />
          <Route path="/portal/marketing-v2/content-editor" element={<MarketingV2InlineContentEditor />} />
          <Route path="/portal/booking-workspace-next" element={<BookingWorkspaceNext />} />
          <Route path="/portal/marketing/portfolio" element={<PortfolioWorkspace />} />
          <Route path="/portal/marketing/layouts/:layoutId/edit" element={<LayoutEditorPage />} />

          {/* Staff Portal */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/portal/*" element={<StaffPortal />} />
        </Routes>
        </Suspense>
        <CustomerExitIntent />
        <StaffPortalCornerLink />
      </BrowserRouter>
      </ViewThroughProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
